<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: ObjectComponent
  Reason: Template of the sm.SmSignatureProfile object component. Left navigation with three sections -
  base data, signer roles, released channel types - one per child entity type plus the profile itself.
-->
@if (initialized && dto) {
    <div class="sm-signature-profile-container">

        <!-- Object header: what is being configured and whether it may leave the office at all -->
        <div class="flex justify-content-between align-items-center p-3 mb-3 surface-card border-round shadow-1">
            <div class="flex align-items-center gap-3">
                <i class="fa-solid fa-file-pen text-2xl text-primary"></i>
                <div>
                    <h4 class="m-0 font-semibold">{{ dto.name }}</h4>
                    <span class="text-sm text-color-secondary">
                        Signaturprofil #{{ dto.id }}
                        @if (dto.documentTypeDtoName) {
                            &middot; {{ dto.documentTypeDtoName }}
                        }
                    </span>
                </div>
            </div>
            <div class="flex align-items-center gap-2">
                @switch (dto.externalSigningMode) {
                    @case (SmExternalSigningModeEnum.active) {
                        <p-tag value="Externe Signatur freigegeben" severity="success"></p-tag>
                    }
                    @case (SmExternalSigningModeEnum.inPreparation) {
                        <p-tag value="Externe Signatur in Vorbereitung" severity="warn"></p-tag>
                    }
                    @default {
                        <p-tag value="Nur Vor-Ort-Unterschrift" severity="secondary"></p-tag>
                    }
                }

                <!--
                  What the profile DEMANDS of the presence route, beside what it releases for the
                  remote one. A demand and never a rating: it says what has to happen before a
                  signature in the caseworker's presence may be taken, not what any signature turned
                  out to be worth. The achieved level is derived on the backend per signing act and no
                  endpoint hands it out, which is why nothing here prints a level beside a finished
                  signature.

                  Only the aes demand is shown. Absent and ses are the SAME configuration on the
                  backend — no identification act demanded — so a second tag for them would announce a
                  decision where none was taken.
                -->
                @if (dto.inPersonEvidenceLevel === SmEvidenceLevelEnum.aes) {
                    <p-tag value="Vor Ort: Identifikation gefordert (AES)"
                           severity="warn"
                           [pTooltip]="'Eine Unterschrift in Anwesenheit wird erst entgegengenommen, wenn die unterzeichnende Person vor Ort identifiziert und der Identifikationsakt protokolliert wurde.'"
                           tooltipPosition="bottom">
                    </p-tag>
                }
            </div>
        </div>

        @if (activeNavigationItems?.action === 'details') {
            <div class="p-3">
                <p-message
                        severity="info"
                        styleClass="w-full mb-3"
                        text="Ein Signaturprofil gehört zu einem Dokumenttyp und legt fest, wie dessen Unterschriftsfelder gelesen und zurückgeschrieben werden. Linkgültigkeit, Einwilligungstextversion, Ziel-URL und die Dokumentgrenzen dürfen jederzeit gepflegt werden, greifen aber erst, wenn die externe Signatur auf 'aktiv' steht — 'in Vorbereitung' verschickt noch keine Einladung.">
                </p-message>

                @if (detailWidget) {
                    <!-- AI: Widget for the sm.SmSignatureProfile record and its external signing settings -->
                    <mvs-widget [widgetData]="detailWidget"></mvs-widget>
                }
            </div>
        }

        @if (activeNavigationItems?.action === 'roles') {
            <div class="p-3">
                <p-message
                        severity="info"
                        styleClass="w-full mb-3"
                        text="Eine Rolle steht für genau einen Unterzeichner. Mehrere Unterschriftsfelder des Dokuments können auf dieselbe Rolle zeigen, weil das Rollenkürzel vor dem Trennzeichen sie gruppiert.">
                </p-message>

                @if (roleWidget) {
                    <!-- AI: Widget for the sm.SmSignatureProfileRole child entity table -->
                    <mvs-widget [widgetData]="roleWidget"></mvs-widget>
                }
            </div>
        }

        @if (activeNavigationItems?.action === 'channelTypes') {
            <div class="p-3">
                <!-- Explanatory only. The evidence level is NEVER an input: it is derived from the
                     identification step that was actually carried out, never from configuration. -->
                <p-message
                        severity="info"
                        styleClass="w-full mb-3"
                        text="Hier wird festgelegt, über welche Kanalvarianten ein Dokument dieses Profils unterschrieben werden darf. Das Beweisniveau lässt sich nicht einstellen: Es wird aus dem tatsächlich durchlaufenen Identifikationsakt abgeleitet. Das Konfigurieren des OTP-Flags macht eine Signatur nicht fortgeschritten — das Durchführen der Verifikation tut es.">
                </p-message>

                @if (channelTypeWidget) {
                    <!-- AI: Widget for the sm.SmSignatureProfileChannelType child entity table -->
                    <mvs-widget [widgetData]="channelTypeWidget"></mvs-widget>
                }
            </div>
        }

    </div>
}
